import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { usePermissions } from '../../hooks/usePermissions';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import Popover, { MenuItem } from '../Popover';
import { Drawer } from '../Dialog/Dialog';
import CommandPalette from './CommandPalette';
import NotificationCenter from './NotificationCenter';
import EjarFillStatus from '../../components/EjarFillStatus';
import { NAV_GROUPS, MOBILE_TABS, ALL_NAV_ITEMS, getBreadcrumbs, sectionPath } from './navigation';
import { freshClick } from './freshNav';
import '../Dialog/dialog.css';
import '../DataTable/datatable.css';
import './appShell.css';

const COLLAPSE_KEY = 'yelo.sidebar.collapsed';

const initials = (user) => {
    const name = (user && (user.name || user.displayName || user.email)) || 'U';
    const parts = String(name).split(/[\s@._-]+/).filter(Boolean);
    return ((parts[0] || 'U')[0] + ((parts[1] || '')[0] || '')).toUpperCase();
};

/** Navigation groups filtered by the signed-in user's permissions. */
export function useVisibleNav() {
    const { currentUser } = useAuth();
    const { hasPermission, viewingAs } = usePermissions();
    // hasPermission is a new function every render; the user's role and permissions are what matter - and the role an
    // admin looks through (without it the menu kept the admin's items after "Exit" - review 3 Oct 2026)
    const seen = viewingAs ? `${viewingAs.roleId}:${viewingAs.permissions.join(',')}` : '';
    const permissionKey = `${currentUser?.role || ''}|${(currentUser?.permissions || []).join(',')}|${seen}`;
    return useMemo(() => NAV_GROUPS
        .map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || hasPermission(i.permission)) }))
        .filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionKey]);
}

function UserMenu({ collapsed }) {
    const { currentUser, logout } = useAuth();
    const { showToast } = useModal();
    const navigate = useNavigate();

    const signOut = async () => {
        try {
            await logout();
        } finally {
            navigate('/login');
            showToast('Signed out', 'success');
        }
    };

    return (
        <Popover
            label="Account"
            align="start"
            width={240}
            className="shell-user-pop"
            trigger={({ props }) => (
                <button {...props} type="button" className="shell-user" aria-label="Account menu">
                    <span className="shell-avatar" aria-hidden="true">{initials(currentUser)}</span>
                    {!collapsed && (
                        <span className="shell-user__text">
                            <span className="shell-user__name">{currentUser?.name || currentUser?.email || 'User'}</span>
                            <span className="shell-user__role">{currentUser?.role || currentUser?.email || ''}</span>
                        </span>
                    )}
                    {!collapsed && <Icon glyph={ICONS.more} size="sm" className="shell-user__more" />}
                </button>
            )}
        >
            {({ close }) => (
                <>
                    <div className="shell-user-card">
                        <div className="shell-user__name">{currentUser?.name || 'User'}</div>
                        <div className="shell-user__role">{currentUser?.email}</div>
                    </div>
                    <div className="ds-menu-divider" />
                    <MenuItem icon={<Icon glyph={ICONS.key} size="sm" />} onClick={() => { close(); navigate('/change-password'); }}>
                        Change password
                    </MenuItem>
                    <MenuItem danger icon={<Icon glyph={ICONS.logout} size="sm" />} onClick={() => { close(); signOut(); }}>
                        Sign out
                    </MenuItem>
                </>
            )}
        </Popover>
    );
}

function SidebarNav({ groups, collapsed, onNavigate }) {
    const navigate = useNavigate();
    return (
        <nav className="shell-nav" aria-label="Main">
            {groups.map((g) => (
                <div key={g.id} className="shell-nav__group">
                    {g.label && !collapsed && (
                        <Link
                            to={sectionPath(g.id)}
                            className="shell-nav__label shell-nav__label--link"
                            title={`All ${g.label} modules`}
                            onClick={freshClick(navigate, sectionPath(g.id), onNavigate)}
                        >
                            {g.icon && <Icon glyph={g.icon} size="xs" />}
                            <span>{g.label}</span>
                        </Link>
                    )}
                    {g.label && collapsed && <div className="shell-nav__rule" aria-hidden="true" />}
                    <ul>
                        {g.items.map((item) => (
                            <li key={item.id}>
                                <NavLink
                                    to={item.path}
                                    end={item.path === '/dashboard'}
                                    className={({ isActive }) => `shell-nav__item ${isActive ? 'is-active' : ''}`}
                                    title={collapsed ? item.label : undefined}
                                    onClick={freshClick(navigate, item.path, onNavigate)}
                                >
                                    <Icon glyph={item.icon} size="sm" />
                                    {!collapsed && <span className="shell-nav__text">{item.label}</span>}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </nav>
    );
}

function Brand({ collapsed }) {
    const navigate = useNavigate();
    return (
        <Link to="/" className="shell-brand" aria-label="Yelo Dubai, go to the home page" onClick={freshClick(navigate, '/')}>
            <img className="shell-brand__mark" src="/assets/yelo-icon.png" alt="" aria-hidden="true" />
            {!collapsed && (
                <span className="shell-brand__text">
                    <span className="shell-brand__name">Yelo Dubai</span>
                    <span className="shell-brand__sub">Fleet operations</span>
                </span>
            )}
        </Link>
    );
}

/**
 * Application shell: fixed sidebar, top bar (breadcrumbs, global search, notifications),
 * mobile drawer and bottom tab bar. Page content renders in <main id="main">.
 */
export default function AppShell({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const groups = useVisibleNav();
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (e) { return false; }
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    useEffect(() => {
        try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch (e) { /* ignore */ }
    }, [collapsed]);

    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

    const openPalette = useCallback(() => setPaletteOpen(true), []);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const crumbs = getBreadcrumbs(location.pathname);
    const visibleItems = groups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));
    const tabs = MOBILE_TABS
        .map((id) => visibleItems.find((i) => i.id === id))
        .filter(Boolean);

    const paletteItems = useMemo(() => [
        ...visibleItems,
        { id: 'act-password', label: 'Change password', icon: ICONS.key, group: 'Account', path: '/change-password' },
        {
            id: 'act-signout',
            label: 'Sign out',
            icon: ICONS.logout,
            group: 'Account',
            run: async () => { try { await logout(); } finally { navigate('/login'); } },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [groups]);

    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

    return (
        <div className={`app-shell ${collapsed ? 'is-collapsed' : ''}`}>
            <a className="skip-link" href="#main">Skip to content</a>

            <aside className="app-shell__sidebar" aria-label="Sidebar">
                <div className="shell-sidebar__top">
                    <Brand collapsed={collapsed} />
                </div>
                <div className="shell-sidebar__scroll">
                    <SidebarNav groups={groups} collapsed={collapsed} />
                </div>
                <div className="shell-sidebar__bottom">
                    <button
                        type="button"
                        className="shell-collapse"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <Icon glyph={collapsed ? ICONS.chevronRight : ICONS.chevronLeft} size="sm" />
                        {!collapsed && <span>Collapse</span>}
                    </button>
                    <UserMenu collapsed={collapsed} />
                    {!collapsed && (
                        <p className="shell-copyright" title="All rights reserved">© 2024–{new Date().getFullYear()} Mohamed Elamir</p>
                    )}
                </div>
            </aside>

            <div className="app-shell__main">
                <header className="app-shell__topbar">
                    <button type="button" className="shell-iconbtn shell-menu-btn" onClick={() => setDrawerOpen(true)} aria-label="Open navigation">
                        <Icon glyph={ICONS.menu} size="sm" />
                    </button>
                    <nav className="shell-crumbs" aria-label="Breadcrumb">
                        <ol>
                            <li><Link to="/" className="shell-crumbs__home" onClick={freshClick(navigate, '/')}>Yelo Dubai</Link></li>
                            {crumbs.map((c, i) => {
                                const last = i === crumbs.length - 1;
                                return (
                                    <li key={`${c.label}-${i}`}>
                                        <Icon glyph={ICONS.chevronRight} size="xs" className="shell-crumbs__sep" />
                                        {last ? (
                                            <span aria-current="page" className="shell-crumbs__current">{c.label}</span>
                                        ) : c.path ? (
                                            <Link to={c.path} onClick={freshClick(navigate, c.path)}>{c.label}</Link>
                                        ) : (
                                            <span>{c.label}</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>
                    <span className="shell-topbar__spacer" />
                    <button type="button" className="shell-search" onClick={openPalette} aria-label="Search modules and actions" aria-keyshortcuts="Control+K Meta+K">
                        <Icon glyph={ICONS.search} size="sm" />
                        <span className="shell-search__text">Search modules and actions…</span>
                        <kbd className="ds-kbd">{isMac ? '⌘' : 'Ctrl'} K</kbd>
                    </button>
                    <button type="button" className="shell-iconbtn shell-search-mobile" onClick={openPalette} aria-label="Search">
                        <Icon glyph={ICONS.search} size="sm" />
                    </button>
                    <NotificationCenter />
                </header>

                <main id="main" className="app-shell__content" tabIndex={-1}>
                    {children}
                </main>
            </div>

            <nav className="app-shell__bottomnav" aria-label="Quick navigation">
                {tabs.map((t) => (
                    <NavLink key={t.id} to={t.path} end={t.path === '/dashboard'} className={({ isActive }) => `shell-tab ${isActive ? 'is-active' : ''}`} onClick={freshClick(navigate, t.path)}>
                        <Icon glyph={t.icon} size="md" />
                        <span>{t.id === 'dashboard' ? 'Today' : t.label.split(' ')[0]}</span>
                    </NavLink>
                ))}
                <button type="button" className="shell-tab" onClick={() => setDrawerOpen(true)}>
                    <Icon glyph={ICONS.menu} size="md" />
                    <span>Menu</span>
                </button>
            </nav>

            <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Menu" size="sm" className="app-shell__drawer shell-drawer">
                <SidebarNav groups={groups} collapsed={false} onNavigate={() => setDrawerOpen(false)} />
                <div className="shell-drawer__user">
                    <UserMenu collapsed={false} />
                </div>
            </Drawer>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />
            {/* a running Fill from Ejar stays visible on every page and in every session */}
            <EjarFillStatus />
        </div>
    );
}

export { ALL_NAV_ITEMS };
